import { dynamicsFromOptions, tipFromOptions, type PaintOptions } from '../brush/types';
import { luminance } from '../core/color';
import { findLayerById } from '../core/layerTree';
import { constrainToOctant } from '../core/math';
import type { DocumentMeta, PaintToolId, Point, RGBA } from '../core/types';
import {
  filterApplier,
  freezeSurface,
  healApplier,
  paintApplier,
  smudgeApplier,
  type EffectContext,
  type StrokeApplier,
} from '../engine/paintEffects';
import { StrokeSession, type StrokeParams } from '../engine/stroke';
import {
  channelStrokeTarget,
  maskStrokeTarget,
  surfaceStrokeTarget,
  type StrokeTarget,
} from '../engine/strokeTarget';
import type { Texture } from '../gl/texture';
import { commitHistory, pickColor } from '../state/actions';
import { liveRender, setLivePreview } from '../state/renderState';
import {
  paintDestination,
  type OverlayContext,
  type PaintDestination,
  type Tool,
  type ToolContext,
  type ToolPointerEvent,
} from './types';

const LABELS: Record<PaintToolId, string> = {
  brush: 'Brush',
  pencil: 'Pencil',
  eraser: 'Eraser',
  clone: 'Clone Stamp',
  blur: 'Blur',
  sharpen: 'Sharpen',
  smudge: 'Smudge',
  dodge: 'Dodge',
  burn: 'Burn',
  sponge: 'Sponge',
  spotHealing: 'Spot Healing',
  healing: 'Healing Brush',
};

const HINTS: Record<PaintToolId, string> = {
  brush:
    'Shift+click draws a straight line from the last dab; Shift+drag locks 0°/45°/90°; Alt samples',
  pencil: 'Shift+click for a straight line; Shift+drag locks 0°/45°/90°; Alt samples a colour',
  eraser: 'Shift+click for a straight stroke; removes pixels, or paints the background on a mask',
  clone: 'Alt+click sets the source; Shift+click paints a straight copy',
  blur: 'Softens pixels; Shift+click draws a straight stroke',
  sharpen: 'Adds local contrast; Shift+click draws a straight stroke',
  smudge: 'Drags colour along the stroke; Shift+click keeps it straight',
  dodge: 'Lightens the chosen tonal range; Shift+click for a straight stroke',
  burn: 'Darkens the chosen tonal range; Shift+click for a straight stroke',
  sponge: 'Saturates or desaturates; Shift+click for a straight stroke',
  spotHealing: 'Paint over a blemish; Shift+click heals in a straight line',
  healing: 'Alt+click sets the source; Shift+click heals in a straight line',
};

/** Tools that need a source point before they can be used. */
const SOURCE_TOOLS = new Set<PaintToolId>(['clone', 'healing']);

/** Only these three carry the foreground colour, so only they can paint a mask. */
const COLOR_TOOLS = new Set<PaintToolId>(['brush', 'pencil', 'eraser']);

interface SourceState {
  point: Point;
  /** Offset kept across strokes while Aligned is on. */
  offset: Point | null;
}

const sources = new Map<string, SourceState>();

function sourceKey(docId: string, id: PaintToolId): string {
  return `${docId}:${id}`;
}

/** A mask holds no colour, so painting it uses the brightness of the colour. */
function greyOf(color: RGBA): RGBA {
  const grey = luminance(color);
  return { r: grey, g: grey, b: grey, a: 1 };
}

function colorApplier(
  id: PaintToolId,
  ctx: ToolContext,
  destination: PaintDestination,
): (effect: EffectContext) => StrokeApplier {
  const erasing = id === 'eraser';
  // On a mask or a channel the eraser has no alpha to remove, so it paints the
  // background colour instead — the same trick Photoshop uses there.
  const color = erasing ? ctx.background : ctx.foreground;
  // Everything single-channel carries brightness, not colour.
  const grey = destination.kind !== 'surface' || destination.colorChannel !== null;
  return (effect) =>
    paintApplier(effect, {
      kind: erasing && destination.kind === 'surface' && !grey ? 'erase' : 'paint',
      color: grey ? greyOf(color) : color,
      blendMode: ctx.paint.blendMode,
      opacity: ctx.paint.opacity,
    });
}

/**
 * Source offset for Clone Stamp and the Healing Brush. Aligned keeps the offset
 * from the first stroke, so several strokes rebuild one continuous copy;
 * unaligned restarts at the sampled point for every stroke.
 */
function sourceOffset(id: PaintToolId, ctx: ToolContext, start: Point): Point | null {
  const key = sourceKey(ctx.doc.id, id);
  const state = sources.get(key);
  if (!state) return null;
  if (ctx.paint.aligned && state.offset) return state.offset;
  const offset = { x: state.point.x - start.x, y: state.point.y - start.y };
  sources.set(key, { ...state, offset });
  return offset;
}

function sampledSurface(ctx: ToolContext, effect: EffectContext): Texture {
  if (!ctx.paint.sampleAllLayers) return effect.target.texture;
  return ctx.engine.compositeToTexture(ctx.doc, { view: ctx.view, showSelection: false });
}

function applierFor(
  id: PaintToolId,
  ctx: ToolContext,
  destination: PaintDestination,
  start: Point,
): ((effect: EffectContext) => StrokeApplier) | null {
  const options: PaintOptions = ctx.paint;

  if (COLOR_TOOLS.has(id)) return colorApplier(id, ctx, destination);

  if (id === 'smudge') return (effect) => smudgeApplier(effect, options.strength);

  if (id === 'spotHealing') {
    return (effect) => healApplier(effect, { offsetX: null, offsetY: null });
  }

  if (id === 'healing') {
    const offset = sourceOffset(id, ctx, start);
    if (!offset) return null;
    return (effect) => healApplier(effect, { offsetX: offset.x, offsetY: offset.y });
  }

  if (id === 'clone') {
    const offset = sourceOffset(id, ctx, start);
    if (!offset) return null;
    return (effect) =>
      filterApplier(effect, {
        filter: 'clone',
        amount: options.opacity,
        range: options.range,
        blurRadius: 0,
        cloneSource: freezeSurface(effect, sampledSurface(ctx, effect)),
        offsetX: offset.x,
        offsetY: offset.y,
      });
  }

  const filters = {
    blur: {
      filter: 'blur' as const,
      amount: options.strength,
      blurRadius: Math.max(1, options.size * 0.15 * (0.3 + options.strength)),
    },
    sharpen: {
      filter: 'sharpen' as const,
      amount: options.strength,
      blurRadius: Math.max(1, options.size * 0.06),
    },
    dodge: { filter: 'dodge' as const, amount: options.exposure, blurRadius: 0 },
    burn: { filter: 'burn' as const, amount: options.exposure, blurRadius: 0 },
    sponge: {
      filter: 'sponge' as const,
      amount: options.sponge === 'saturate' ? options.strength : -options.strength,
      blurRadius: 0,
    },
  };
  const preset = filters[id as keyof typeof filters];
  if (!preset) return null;
  return (effect) => filterApplier(effect, { ...preset, range: options.range });
}

function strokeParams(
  id: PaintToolId,
  ctx: ToolContext,
  applier: (effect: EffectContext) => StrokeApplier,
  pointerType: ToolPointerEvent['pointerType'],
): StrokeParams {
  const options = ctx.paint;
  return {
    size: options.size,
    tip: tipFromOptions(options),
    dynamics: dynamicsFromOptions(options),
    flow: options.flow,
    smoothing: options.smoothing,
    // A mouse reports a constant pressure, which would silently halve every stroke.
    usePressure: pointerType === 'pen',
    sharp: id === 'pencil',
    applier,
  };
}

function createTarget(ctx: ToolContext, destination: PaintDestination): StrokeTarget {
  const resources = ctx.engine.resourcesFor(ctx.doc);
  if (destination.kind === 'mask') return maskStrokeTarget(resources, destination.id);
  if (destination.kind === 'channel') return channelStrokeTarget(resources, destination.id);
  return surfaceStrokeTarget(resources, destination.id, destination.colorChannel);
}

/** Linked masks share the layer's contentOffset; unlinked masks stay document-aligned. */
function strokeContentOffset(doc: DocumentMeta, destination: PaintDestination): Point {
  if (destination.kind === 'channel') return { x: 0, y: 0 };
  const layer = findLayerById(doc.layers, destination.id);
  if (!layer) return { x: 0, y: 0 };
  if (destination.kind === 'mask' && !layer.maskLinked) return { x: 0, y: 0 };
  return layer.contentOffset ?? { x: 0, y: 0 };
}

/**
 * Every brush-driven tool is the same engine with a different applier, so they
 * share one implementation: the tip, the dynamics and the destination all come
 * from the options, and only the meaning of the coverage changes.
 */
function createPaintTool(id: PaintToolId): Tool {
  let session: StrokeSession | null = null;
  let destination: PaintDestination | null = null;
  /** True while Alt is held after a colour sample press (eyedropper drag). */
  let altSampling = false;
  /** End of the last finished stroke in this document — Shift+click continues from here. */
  const lastEnds = new Map<string, Point>();
  /** Anchor for Shift+drag 0°/45°/90° constraining during the active stroke. */
  let strokeOrigin: Point | null = null;
  /** Tip of the active stroke, remembered so Shift+click has a reliable endpoint. */
  let strokeTip: Point | null = null;
  /** True while Shift is held over the canvas with no button down: preview the chord. */
  let straightPreview = false;

  const release = (): void => {
    if (destination) setLivePreview(destination.kind, destination.id, null);
    session = null;
    destination = null;
    altSampling = false;
    strokeOrigin = null;
    strokeTip = null;
    straightPreview = false;
  };

  const sampleColour = (point: Point, ctx: ToolContext): void => {
    pickColor(point, ctx.options.eyedropperFromComposite);
  };

  const paintPoint = (event: ToolPointerEvent, origin: Point | null): Point =>
    event.shift && origin ? constrainToOctant(origin, event.point) : event.point;

  const finish = (ctx: ToolContext): void => {
    if (!session) return;
    const tip = strokeTip;
    const painted = session.hasContent;
    const ops = session.commit();
    if (painted && tip) lastEnds.set(ctx.doc.id, { ...tip });
    const label = destination && destination.kind !== 'surface'
      ? `${LABELS[id]} on ${destination.kind === 'mask' ? 'Mask' : 'Channel'}`
      : LABELS[id];
    release();
    commitHistory(ctx.doc.id, label, ops);
    ctx.requestRender();
  };

  return {
    id,
    // Keep a system cursor visible: the tip ring is drawn in the overlay, but
    // `none` leaves some users with no pointer at all (before first move, or
    // when the overlay pass is skipped).
    cursor: 'crosshair',
    hint: HINTS[id],

    onPointerDown(event, ctx) {
      // Alt sets the source for the copying tools and samples a colour for the rest.
      if (event.alt) {
        if (SOURCE_TOOLS.has(id)) {
          sources.set(sourceKey(ctx.doc.id, id), { point: event.point, offset: null });
          ctx.setStatus(`${LABELS[id]} source set`);
        } else {
          altSampling = true;
          liveRender.brushCursor = null;
          sampleColour(event.point, ctx);
          ctx.requestRender();
        }
        return;
      }

      // Only colour tools can write into a mask or a channel; a filter needs
      // real pixels, so it always falls back to the layer.
      const colorTool = COLOR_TOOLS.has(id);
      const target = paintDestination(
        ctx.doc,
        colorTool ? ctx.paintTarget : 'layer',
        colorTool ? ctx.activeChannel : { kind: 'composite' },
      );
      if (!target) {
        const layer = findLayerById(ctx.doc.layers, ctx.doc.activeLayerId);
        const maskLocked =
          layer &&
          (ctx.paintTarget === 'mask' || ctx.activeChannel.kind === 'mask') &&
          layer.lockMask;
        ctx.setStatus(
          maskLocked
            ? 'The layer mask is locked'
            : 'The active layer is locked, or has no pixels to paint on',
        );
        return;
      }

      const lastEnd = lastEnds.get(ctx.doc.id) ?? null;
      // Photoshop: Shift+click paints a straight segment from the previous dab
      // at any angle. Shift+drag (no prior dab) starts here and locks octants.
      const continueStraight = Boolean(event.shift && lastEnd);
      const applier = applierFor(id, ctx, target, event.point);
      if (!applier) {
        ctx.setStatus(`Alt+click sets the source for the ${LABELS[id]}`);
        return;
      }

      destination = target;
      // The octant anchor is where the pointer went down, never the old dab:
      // once the chord is drawn, a shaky hand must not re-snap it to 45°.
      strokeOrigin = event.point;
      strokeTip = event.point;
      const params = strokeParams(id, ctx, applier, event.pointerType);
      // Smoothing would bow a Shift+click chord; keep the segment geometric.
      if (continueStraight) params.smoothing = 0;
      session = new StrokeSession(
        ctx.engine,
        ctx.doc,
        createTarget(ctx, target),
        params,
        strokeContentOffset(ctx.doc, target),
      );
      setLivePreview(target.kind, target.id, session.previewTexture);
      if (continueStraight) {
        session.begin(lastEnd!, event.pressure, false);
        session.extend(event.point, event.pressure);
      } else {
        session.begin(event.point, event.pressure);
      }
      ctx.requestRender();
    },

    onPointerMove(event, ctx) {
      // Holding Alt turns the brush into an eyedropper; hide the tip ring so the
      // crosshair cursor is what the user aims with.
      if (event.alt && !SOURCE_TOOLS.has(id)) {
        liveRender.brushCursor = null;
        if (altSampling) sampleColour(event.point, ctx);
        ctx.requestRender();
        return;
      }
      altSampling = false;
      straightPreview = !session && event.shift;
      const point = paintPoint(event, session ? strokeOrigin : null);
      liveRender.brushCursor = {
        point,
        radius: ctx.paint.size / 2,
        roundness: ctx.paint.roundness,
        angle: ctx.paint.angle,
      };
      if (session) {
        strokeTip = point;
        session.extend(point, event.pressure);
      }
      ctx.requestRender();
    },

    onPointerUp(_event, ctx) {
      if (altSampling) {
        altSampling = false;
        ctx.requestRender();
        return;
      }
      finish(ctx);
    },

    cancel(ctx) {
      if (!session) return;
      session.cancel();
      release();
      ctx.requestRender();
    },

    drawOverlay(overlay, ctx) {
      if (SOURCE_TOOLS.has(id)) {
        const source = sources.get(sourceKey(ctx.doc.id, id));
        if (source) drawCross(overlay, source.point);
      }

      const cursor = liveRender.brushCursor;
      if (!cursor) return;
      const center = overlay.toScreen(cursor.point);
      const radius = cursor.radius * overlay.zoom;
      const { ctx: c } = overlay;

      // Shift+click is invisible state until it fires, so show the chord it
      // would paint from the last dab to the pointer.
      const anchor = !session && straightPreview ? lastEnds.get(ctx.doc.id) : undefined;
      if (anchor) {
        const from = overlay.toScreen(anchor);
        c.save();
        c.strokeStyle = 'rgba(255,255,255,0.75)';
        c.lineWidth = 1;
        c.setLineDash([4, 4]);
        c.beginPath();
        c.moveTo(from.x, from.y);
        c.lineTo(center.x, center.y);
        c.stroke();
        c.restore();
      }

      if (radius < 3) {
        c.strokeStyle = 'rgba(255,255,255,0.9)';
        c.lineWidth = 1;
        c.beginPath();
        c.moveTo(center.x - 6, center.y);
        c.lineTo(center.x + 6, center.y);
        c.moveTo(center.x, center.y - 6);
        c.lineTo(center.x, center.y + 6);
        c.stroke();
        return;
      }

      // The outline shows the real tip: squeezed by the roundness and rotated by
      // the angle, so a flat brush can be aimed before the stroke starts.
      c.beginPath();
      c.ellipse(
        center.x,
        center.y,
        radius,
        radius * cursor.roundness,
        (cursor.angle * Math.PI) / 180,
        0,
        Math.PI * 2,
      );
      // Two passes so the outline stays visible on light and dark pixels alike.
      c.strokeStyle = 'rgba(0,0,0,0.7)';
      c.lineWidth = 3;
      c.stroke();
      c.strokeStyle = 'rgba(255,255,255,0.9)';
      c.lineWidth = 1;
      c.stroke();
    },
  };
}

/** Marks where a copying tool takes its pixels from. */
function drawCross(overlay: OverlayContext, point: Point): void {
  const at = overlay.toScreen(point);
  const { ctx: c } = overlay;
  const path = (): void => {
    c.beginPath();
    c.moveTo(at.x - 8, at.y);
    c.lineTo(at.x + 8, at.y);
    c.moveTo(at.x, at.y - 8);
    c.lineTo(at.x, at.y + 8);
    c.stroke();
  };
  c.strokeStyle = 'rgba(0,0,0,0.7)';
  c.lineWidth = 3;
  path();
  c.strokeStyle = 'rgba(120,190,255,0.95)';
  c.lineWidth = 1;
  path();
}

export const brushTool = createPaintTool('brush');
export const pencilTool = createPaintTool('pencil');
export const eraserTool = createPaintTool('eraser');
export const cloneTool = createPaintTool('clone');
export const blurTool = createPaintTool('blur');
export const sharpenTool = createPaintTool('sharpen');
export const smudgeTool = createPaintTool('smudge');
export const dodgeTool = createPaintTool('dodge');
export const burnTool = createPaintTool('burn');
export const spongeTool = createPaintTool('sponge');
export const spotHealingTool = createPaintTool('spotHealing');
export const healingTool = createPaintTool('healing');
